import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import { toggleSpinner, updateMessages } from 'apps/auth/actions/appAction';
import { addCardData } from 'apps/auth/actions/cardsAction';
import { addExtras } from 'apps/auth/actions/extraAction';
import { setNewForm, updateFields, changeFormFieldValue } from 'apps/auth/actions/formAction';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import { Button } from 'apps/auth/components/common/Button/Button';
import { FixedBottomPanel } from 'apps/auth/components/common/Layout/FixedBottomPanel/FixedBottomPanel';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { Row } from 'apps/auth/components/common/Layout/Row';
import InputAutoDetect from 'apps/auth/components/form/InputAutoDetect';
import NewCardField from 'apps/auth/components/form/NewCardField';
import Form from 'apps/auth/components/form-multi/Form';
import ModalComponent from 'apps/auth/components/utils/ModalComponent';
import { CARD_CVV, CARD_EXP_DATE, PAN } from 'apps/auth/constants/fieldsName';
import { EXP_DATE } from 'apps/auth/constants/FieldType';
import { ADD_SOURCE_BANK_CARD, PLUS_SUBSCRIBE_FORM_NAME } from 'apps/auth/constants/formName';
import { AUTH_PLUS_SUBSCRIBE_ACTION } from 'apps/auth/constants/UrlConstants';
import { apiFetchData } from 'apps/auth/helpers/api';
import { logWarn } from 'apps/auth/helpers/clientLogger';
import { isObject } from 'apps/auth/helpers/utils';
import { usePageMessages } from 'apps/auth/hooks/selectors';

import PaysendPlusPrecheckPopup from './PaysendPlusPrecheckPopup';

const AddNewCardModal = (props) => {
    const { cardsAddBankCardTitle, cardsAddBankCardText, cardsAddBankCardButton } = usePageMessages();

    const submitRef = useRef(null);

    useEffect(() => {
        if (!isObject(props.form)) {
            props.toggleSpinner(true);
            apiFetchData('/auth/plus/add/card.screen')
                .then((json) => {
                    if (json.messages) {
                        props.updatePageMessages(json.messages);
                    }

                    props
                        .addCardData(json.extra.cardForm.card)
                        .then(() => props.setNewForm(ADD_SOURCE_BANK_CARD, getFields(json.extra.cardForm)).then(() => props.toggleSpinner(false)));
                })
                .catch((e) => logWarn(e))
                .finally(() => props.toggleSpinner(false));
        }
        return () => {
            if (isObject(props.form) && isObject(props.form.fields)) {
                const clearFields = { ...props.form.fields };
                Object.values(clearFields).forEach((field) => {
                    field.value = '';
                    field.isError = false;
                });
                props.updateFields(ADD_SOURCE_BANK_CARD, clearFields);
            }
        };
    }, []);

    const getFields = (cardForm) => {
        const fields = {};
        if (isObject(cardForm)) {
            const card = cardForm.card;
            fields[card.pan.name] = card.pan;
            if (isObject(card.cardExpired)) {
                fields[card.cardExpired.name] = card.cardExpired;
            }
            if (isObject(card.cvv)) {
                fields[card.cvv.name] = card.cvv;
            }

            if (Array.isArray(cardForm.otherFields)) {
                cardForm.otherFields.forEach((otherField) => (fields[otherField.name] = otherField));
            }
        }
        return fields;
    };

    const addField = (fieldName) => {
        return fieldName !== EXP_DATE && fieldName !== 'cvv' && fieldName !== PAN;
    };

    const submitHandler = (_fields, data) => {
        if (data.messages) {
            props.updatePageMessages(data.messages);
        }
        if (data.messageBodyMessages) {
            props.updateMessages(data.messageBodyMessages);
        }
        if (data.extra && data.extra.precheque) {
            props.addExtras(data.extra);
            props
                .changeFormFieldValue(PLUS_SUBSCRIBE_FORM_NAME, data.forms[PLUS_SUBSCRIBE_FORM_NAME].fields['pMethodEncodedInfo'].value, 'pMethodEncodedInfo')
                .then(() => props.changeFormFieldValue(PLUS_SUBSCRIBE_FORM_NAME, data.forms[PLUS_SUBSCRIBE_FORM_NAME].fields['pMethodId'].value, 'pMethodId'))
                .then(() =>
                    props.popupRef?.current?.open(
                        <PaysendPlusPrecheckPopup
                            popupRef={props.popupRef}
                            actionType={'subscription'}
                            action={AUTH_PLUS_SUBSCRIBE_ACTION}
                            tariff={props.tariff}
                            submitRef={submitRef}
                            selectedCard={data.extra.selectedCard}
                        />,
                    ),
                );
        }

        props.popupRef?.current?.close();
    };

    if (isObject(props.form)) {
        return (
            <ModalComponent hasBottomPanel>
                <>
                    <HeaderPanel title={<>{cardsAddBankCardTitle}</>} subtitle={cardsAddBankCardText} />
                    <Form
                        getCanonicalPath={true}
                        formName={ADD_SOURCE_BANK_CARD}
                        fields={props.form.fields}
                        onSubmitHandler={submitHandler}
                        action={'/auth/plus/add/card.action' + '?postAction=' + props.postAction}
                    >
                        <Row indent="adaptive-32-48">
                            <NewCardField
                                binExtraName="binInfo"
                                formName={ADD_SOURCE_BANK_CARD}
                                panName={PAN}
                                redirectByBinUrl={false}
                                cvvName={CARD_CVV}
                                expiredDateName={CARD_EXP_DATE}
                            />
                        </Row>
                        {Object.values(props.form.fields).map((field) => {
                            if (addField(field.name)) {
                                return (
                                    <Row indent="adaptive-24-32" key={field.name}>
                                        <InputAutoDetect formName={ADD_SOURCE_BANK_CARD} field={field} />
                                    </Row>
                                );
                            } else {
                                return false;
                            }
                        })}
                        <FixedBottomPanel isRightAligned>
                            <Button type="submit" raised={true} caption={cardsAddBankCardButton} />
                        </FixedBottomPanel>
                    </Form>
                </>
            </ModalComponent>
        );
    } else {
        return false;
    }
};

const mapStateToProps = (state) => {
    return {
        form: state.form[ADD_SOURCE_BANK_CARD],
    };
};

export default connect(mapStateToProps, {
    addExtras,
    addCardData,
    toggleSpinner,
    setNewForm,
    updateMessages,
    updatePageMessages,
    updateFields,
    changeFormFieldValue,
})(AddNewCardModal);
